'use client';

import React from 'react';
import { Chip, Tooltip } from 'src/components';
import { CUSTOM_ROLE_COPY } from './constants';

export interface CustomRoleChipProps {
  /**
   * Whether to wrap the chip in its explanation. Turn it off inside a `Select`:
   * the same node renders in the option list *and* in the closed trigger, where
   * a tooltip that follows the selected value around reads as a bug.
   */
  withTooltip?: boolean;
  'data-testid'?: string;
}

/**
 * @description Marks a role as company-built rather than one of MedScout's
 *              standard roles (MKT-530). Rendered wherever roles are listed or
 *              assigned, so the caller only has to decide *whether* a role is
 *              custom — see `isCustomRole`.
 * @returns React.ReactElement
 */
const CustomRoleChip: React.FC<CustomRoleChipProps> = ({
  withTooltip = true,
  'data-testid': dataTestId = 'custom-role-chip',
}) => {
  // Soft blue: magenta read as an alert on a marker that is purely
  // informational, and teal was brighter than the job needs. This is `Chip`'s
  // own `soft`/`primary`, so no class override — the earlier worry about a blue
  // chip vanishing into the sky-tinted selected row in RolesAdmin no longer
  // applies now that lists group roles into sections instead of tagging them.
  //
  // `shrink-0` throughout: the chip sits beside a truncating role label, and it
  // is the label that should give up room, not the marker.
  const chip = (
    <Chip
      size="sm"
      variant="soft"
      color="primary"
      className="shrink-0"
      data-testid={dataTestId}
    >
      {CUSTOM_ROLE_COPY.label}
    </Chip>
  );

  if (!withTooltip) return chip;

  return (
    <Tooltip
      content={CUSTOM_ROLE_COPY.tooltip}
      delay={200}
      className="shrink-0"
      slotProps={{ contentClassName: 'max-w-xs whitespace-normal text-left' }}
    >
      {chip}
    </Tooltip>
  );
};

export default CustomRoleChip;
